import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { useAuth } from '../../hooks/useAuth';

export const ProtectedRoute = ({ children }: React.PropsWithChildren) => {
  const { isAuthenticated, user, isHydrated } = useStore();
  const { isAuthChecking, isRoleVerified } = useAuth();
  const location = useLocation();

  // Show loading while store is hydrating
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
          <p className="text-sm text-slate-500">Memuat aplikasi...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Tunggu auth checking selesai sebelum redirect
  // Ini mencegah redirect ke login sebelum data profile selesai di-load
  if (isAuthChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
          <p className="text-sm text-slate-500">Memverifikasi autentikasi...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login but save the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // CRITICAL: Check admin status EARLY - but only trust persisted role if a stored token exists
  // This avoids stale localStorage roles (e.g., leftover admin role) from redirecting users incorrectly.
  const hasAuthToken =
    typeof localStorage !== 'undefined' && !!localStorage.getItem('supabase.auth.token');
  // Only use the quick admin redirect when we have an auth token present locally
  if (user?.role && hasAuthToken) {
    const isAdmin =
      user.role === 'admin' ||
      user.role === 'admin_smp' ||
      user.role === 'admin_smk' ||
      user.role === 'admin_ponpes' ||
      user.role.startsWith('admin_'); // Fallback for any other admin_* roles

    // ALL Admin users (including admin_smp, admin_smk, admin_ponpes):
    // Skip complete-profile check entirely, redirect away if on complete-profile page
    // Complete-profile is ONLY for students
    // This check happens BEFORE role verification to prevent any flash
    if (isAdmin) {
      if (location.pathname === '/complete-profile') {
        return <Navigate to="/admin" replace />;
      }
      // Admin users can access any other protected route without profile completion check
      // Don't wait for role verification - admin status from localStorage is sufficient
      return <>{children}</>;
    }
  }

  // CRITICAL: For non-admin users, tunggu role verification selesai sebelum check isProfileCompleted
  // Ini memastikan isProfileCompleted sudah ter-update dari database, bukan dari localStorage
  // Jangan check isProfileCompleted jika role belum di-verify (data mungkin stale)
  if (!isRoleVerified || !user?.role) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
          <p className="text-sm text-slate-500">Memuat data profil...</p>
        </div>
      </div>
    );
  }

  // Non-admin users: Check if profile is completed
  // CRITICAL: Only check after role is verified (ensures isProfileCompleted is from database)
  if (user && !user.isProfileCompleted && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  // If profile IS completed and we try to access complete-profile page, redirect to dashboard
  if (user && user.isProfileCompleted && location.pathname === '/complete-profile') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
